import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Users, Trophy, MapPin, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const HERO_IMAGE = "https://images.unsplash.com/photo-1720799359333-974848b7dd8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHx5b3V0aCUyMHRyYWNrJTIwYW5kJTIwZmllbGQlMjBydW5uaW5nJTIwcmFjZSUyMGRpdmVyc2UlMjBraWRzJTIwYXRobGV0aWNzfGVufDB8fHx8MTc3MjU2MjcyMXww&ixlib=rb-4.1.0&q=85";
const IMPACT_BG = "https://images.unsplash.com/photo-1700914300017-0b90e4fa8773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHx5b3V0aCUyMHRyYWNrJTIwYW5kJTIwZmllbGQlMjBydW5uaW5nJTIwcmFjZSUyMGRpdmVyc2UlMjBraWRzJTIwYXRobGV0aWNzfGVufDB8fHx8MTc3MjU2MjcyMXww&ixlib=rb-4.1.0&q=85";
const VOLUNTEER_IMAGE = "https://images.unsplash.com/photo-1714176966820-d5d9baee2ca6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwdm9sdW50ZWVyJTIwZ3JvdXAlMjB0ZWFtJTIwYXRobGV0aWNzJTIwbm9ucHJvZml0fGVufDB8fHx8MTc3MjU2MjcyM3ww&ixlib=rb-4.1.0&q=85";

const impactStats = [
  { number: 500, suffix: '+', label: 'Athletes Supported', icon: Users },
  { number: 25, suffix: '', label: 'Programs Funded', icon: Trophy },
  { number: 150, suffix: '+', label: 'Scholarships Awarded', icon: Heart },
  { number: 12, suffix: '', label: 'Communities Reached', icon: MapPin },
];

const programs = [
  {
    title: 'Equipment Fund',
    description: 'Providing essential sports equipment to young athletes who cannot afford it.',
    icon: '🏃',
    color: 'bg-red-50',
  },
  {
    title: 'Registration Assistance',
    description: 'Covering league fees, registration costs, and travel expenses for competitions.',
    icon: '📝',
    color: 'bg-slate-50',
  },
  {
    title: 'Training Programs',
    description: 'Professional coaching and training opportunities for underprivileged youth.',
    icon: '🏆',
    color: 'bg-red-50',
  },
];

const AnimatedCounter = ({ target, suffix }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [target]);

  return (
    <span className="font-heading text-5xl md:text-6xl font-bold text-red-600">
      {count.toLocaleString()}{suffix}
    </span>
  );
};

const Home = () => {
  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight">
              Every Child Deserves to Run in{' '}
              <span className="text-red-500">Lane 4.</span>
            </h1>
            <p className="mt-6 text-xl text-slate-200 max-w-2xl">
              Removing financial barriers in youth athletics so every child has access to opportunity.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/donate" data-testid="hero-donate-btn">
                <Button className="bg-red-600 text-white hover:bg-red-700 rounded-none uppercase tracking-widest font-bold px-8 py-6 text-base w-full sm:w-auto">
                  Donate Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/sponsors" data-testid="hero-sponsor-btn">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-none uppercase tracking-widest font-bold px-8 py-6 text-base w-full sm:w-auto">
                  Sponsor an Athlete
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-2 h-3 bg-white rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Our Mission</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
                Leveling the Playing Field for Every Young Athlete
              </h2>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                At Lane 4 For All Kids Foundation, we believe that financial circumstances should never determine a child's ability to participate in sports. We work tirelessly to eliminate barriers and create equitable opportunities for children to participate, compete, and thrive.
              </p>
              <div className="mt-8 flex items-center space-x-6">
                <Link to="/about" data-testid="mission-learn-more-btn">
                  <Button className="bg-slate-900 text-white hover:bg-red-600 rounded-none uppercase tracking-widest font-bold px-8 py-4">
                    Learn More
                    <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-none overflow-hidden img-zoom">
                <img
                  src={IMPACT_BG}
                  alt="Young athletes on track"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-red-600 text-white p-6 hidden md:block">
                <p className="font-heading text-4xl font-bold">501(c)(3)</p>
                <p className="text-red-200 text-sm">Status Pending</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="impact-stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">Our Impact</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              Making a Difference Together
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="impact-card bg-white p-8 text-center border border-slate-200 hover:shadow-lg transition-shadow"
                data-testid={`impact-stat-${index}`}
              >
                <stat.icon className="w-8 h-8 text-red-600 mx-auto mb-4" />
                <AnimatedCounter target={stat.number} suffix={stat.suffix} />
                <p className="mt-2 text-slate-600 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Preview Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold uppercase tracking-widest text-sm">What We Do</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-4">
              Our Programs
            </h2>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
              We provide comprehensive support to ensure every child can pursue their athletic dreams.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={program.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`program-card ${program.color} p-8 border border-slate-200`}
                data-testid={`program-card-${index}`}
              >
                <div className="text-4xl mb-4">{program.icon}</div>
                <h3 className="font-heading text-xl font-bold text-slate-900">{program.title}</h3>
                <p className="mt-3 text-slate-600">{program.description}</p>
                <Link
                  to="/programs"
                  className="inline-flex items-center mt-6 text-red-600 font-bold text-sm uppercase tracking-wider hover:text-slate-900 transition-colors"
                >
                  Learn More
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Lane 4 Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="why-lane4-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="aspect-video rounded-none overflow-hidden">
                <img
                  src={VOLUNTEER_IMAGE}
                  alt="Team working together"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-red-500 font-bold uppercase tracking-widest text-sm">Why Lane 4?</span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mt-4">
                In Track, Lane 4 is Prime Position
              </h2>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed">
                In competitive track and field, Lane 4 is considered the prime position—the lane where champions run. We believe every child deserves that same advantage, regardless of their family's financial situation.
              </p>
              <ul className="mt-8 space-y-4">
                {['Equal opportunity for all young athletes', 'Breaking down financial barriers', 'Building confidence through competition', 'Creating pathways to success'].map((item, i) => (
                  <li key={i} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-red-600 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-slate-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsor CTA Banner */}
      <section className="py-20 md:py-24 bg-red-600 cta-banner" data-testid="sponsor-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Become a Corporate Partner
          </h2>
          <p className="mt-4 text-xl text-red-100 max-w-2xl mx-auto">
            Join leading organizations in supporting the next generation of athletes. Corporate sponsorships start at $1,000.
          </p>
          <Link to="/sponsors" className="inline-block mt-8" data-testid="sponsor-cta-btn">
            <Button className="bg-white text-red-600 hover:bg-slate-900 hover:text-white rounded-none uppercase tracking-widest font-bold px-10 py-6 text-base">
              Explore Sponsorship
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Donation CTA Banner */}
      <section className="py-20 md:py-24 bg-slate-900" data-testid="donate-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                Your Donation Changes Lives
              </h2>
              <p className="mt-4 text-lg text-slate-300">
                Every dollar helps a child access the equipment, training, and opportunities they need to succeed. Join our community of supporters today.
              </p>
            </div>
            <div className="text-center lg:text-right">
              <Link to="/donate" data-testid="donate-cta-btn">
                <Button className="bg-red-600 text-white hover:bg-red-700 animate-pulse-slow rounded-full px-12 py-6 font-bold text-lg shadow-xl">
                  <Heart className="mr-2 w-5 h-5" />
                  Make a Donation
                </Button>
              </Link>
              <p className="mt-4 text-slate-400 text-sm">
                501(c)(3) status pending. Donations may not yet be tax-deductible.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
